import React, { useEffect, useState } from "react";
import { Box, Typography, List, ListItem, Avatar, Badge } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import AnalyticsTracker from "../../GASetup/AnalyticsTracker";
import LoadingAnimation from "../../Animations/LoadingAnimation";
import FormatTime from "../../AdsPage/FormatTime";
import { fonts, headingFonts, notFoundBox } from "../../../Styles/Styles";

const Inbox = ({ userId }) => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchChats = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/chat/inbox/${userId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await response.json();
        setChats(data); // Store the chats in the state
        setLoading(false);
      } catch (error) {
        console.error("Error fetching chats:", error);
        setLoading(false);
      }
    };

    fetchChats();
  }, [userId, token]);

  const handleChatClick = async (chatId) => {
    // Mark messages as read when user opens the chat
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/chat/mark-read/${chatId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId }),
        }
      );
      const result = await response.json();
      if (result.success) {
        navigate(`/chat/${chatId}`); // Redirect to the chat page
      }
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };
  console.log(chats);

  if (loading) {
    return <LoadingAnimation />;
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Helmet>
        <title>Inbox - Listed.ky</title>
        <meta name="description" content="Inbox - Listed.ky" />
      </Helmet>
      <AnalyticsTracker />
      <Typography variant="h4" gutterBottom sx={{ ...headingFonts }}>
        Inbox
      </Typography>

      {chats.length === 0 ? (
        <Box sx={{ ...notFoundBox }}>
          <SearchOffOutlinedIcon
            sx={{
              color: "var(--primary-color)",
              fontSize: 50,
              opacity: 0.5,
              marginBottom: 1,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: "var(--primary-color)",
              ...headingFonts,
            }}
          >
            You have no chats yet.
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, ...fonts }}>
            Start a conversation by clicking "Start Chat" from Ad.
          </Typography>
        </Box>
      ) : (
        <List>
          {chats.map((chat) => {
            const otherParticipant = chat.participants.find(
              (participant) => participant._id !== userId
            );
            const lastMessage =
              chat.messages?.[chat.messages.length - 1] || null;

            const truncateText = (text) => {
              if (!text) return "No messages yet";

              const words = text.split(" ").slice(0, 4).join(" "); // Limit to 4 words
              return words.length > 20 ? words.substring(0, 20) + "..." : words;
            };

            const unreadMessagesCount = chat.messages.filter(
              (msg) =>
                !msg.readBy.includes(userId) && msg.sender?._id !== userId
            ).length;

            return (
              <ListItem
                button
                key={chat._id}
                onClick={() => handleChatClick(chat._id)}
                sx={{
                  marginBottom: 2,
                  cursor: "pointer",
                  borderBottom: "1px solid #ddd",
                  display: "flex",
                  alignItems: "center",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              >
                <Badge
                  badgeContent={unreadMessagesCount}
                  sx={{
                    marginRight: 2,
                    "& .MuiBadge-badge": {
                      backgroundColor: "#f15a25",
                      color: "white",
                    },
                  }}
                >
                  <Avatar
                    src={
                      otherParticipant?.image
                        ? otherParticipant.image
                        : "https://elasticbeanstalk-us-east-1-908027412287.s3.us-east-1.amazonaws.com/uploads/ProfileImages/default.png"
                    }
                    alt={otherParticipant?.name || "Default Avatar"}
                    sx={{ width: 50, height: 50 }}
                  />
                </Badge>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1" sx={{ ...headingFonts }}>
                    {otherParticipant?.firstName || "User Not Found"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    noWrap
                    sx={{ ...fonts }}
                  >
                    {truncateText(
                      lastMessage ? lastMessage.content : "No messages yet"
                    )}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    ml: 2,
                    ...fonts,
                  }}
                >
                  {lastMessage && (
                    <FormatTime createdAt={lastMessage.timestamp} />
                  )}
                </Typography>
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default Inbox;
